import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client.js'
import { useAuth } from '../context/AuthContext.jsx'
import { useToast } from '../context/ToastContext.jsx'
import WeaponArt from '../components/WeaponArt.jsx'
import CoinIcon from '../components/CoinIcon.jsx'
import { useCatalog } from '../hooks/useCatalog.js'
import {
  formatRub,
  formatDate,
  plural,
  sourceLabel,
  RARITY_LABELS,
  WEAR_SHORT,
} from '../lib/format.js'
import '../styles/profile.css'

const TX_LABELS = {
  deposit: 'Пополнение',
  sell: 'Продажа предметов',
  promo: 'Промокод',
  bonus: 'Бонус',
  case: 'Открытие кейса',
  upgrade: 'Апгрейд',
}

function txLabel(type) {
  if (type?.startsWith('case')) return 'Открытие кейса'
  return TX_LABELS[type] ?? type
}

export default function Profile() {
  const { user, logout } = useAuth()
  const toast = useToast()
  const { getItem } = useCatalog()

  const [stats, setStats] = useState(null)
  const [error, setError] = useState(null)
  const [tradeUrl, setTradeUrl] = useState(user?.tradeUrl || '')
  const [savingTradeUrl, setSavingTradeUrl] = useState(false)

  useEffect(() => {
    api.stats().then(setStats).catch((err) => setError(err.message))
    if (user?.tradeUrl) {
      setTradeUrl(user.tradeUrl)
    } else {
      api.getTradeUrl().then(({ tradeUrl: url }) => {
        if (url) setTradeUrl(url)
      }).catch(() => {})
    }
  }, [user])

  async function handleSaveTradeUrl(e) {
    e.preventDefault()
    if (!tradeUrl.trim()) {
      toast.error('Укажите ссылку на обмен')
      return
    }
    setSavingTradeUrl(true)
    try {
      await api.saveTradeUrl(tradeUrl.trim())
      toast.success('Trade URL успешно сохранён!')
    } catch (err) {
      toast.error(err.message)
    } finally {
      setSavingTradeUrl(false)
    }
  }

  const bestItem = useMemo(
    () => (stats?.bestDrop ? getItem(stats.bestDrop.itemId) : null),
    [stats, getItem],
  )

  if (!user) return null

  const wagered = stats?.totalWagered ?? 0
  const won = stats?.totalWon ?? 0
  const ratio = wagered > 0 ? won / wagered : 0
  const net = won - wagered

  return (
    <div className="page container profile-page">
      <div className="page-head profile-head-flex">
        <div className="profile-user-info">
          {user.avatar ? (
            <img src={user.avatar} alt={user.username} className="profile-large-avatar" />
          ) : (
            <div className="profile-large-avatar profile-large-avatar-fallback">
              {user.username.slice(0, 1).toUpperCase()}
            </div>
          )}
          <div>
            <span className="eyebrow">{user.steamId ? 'Аккаунт Steam' : 'Аккаунт'}</span>
            <h1>{user.username}</h1>
            <p>
              {user.steamId ? `Steam ID: ${user.steamId}` : user.email} · с нами с {formatDate(user.createdAt)}
            </p>
          </div>
        </div>
        <div className="row wrap">
          <Link to="/deposit" className="btn btn-primary">
            Пополнить баланс
          </Link>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              logout()
              toast.info('Вы вышли из аккаунта')
            }}
          >
            Выйти
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="stat-grid">
        <div className="card card-pad stat">
          <span className="tiny muted">Баланс</span>
          <span className="stat-value" style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
            <span>{formatRub(user.balance)}</span>
            <CoinIcon size="sm" />
          </span>
        </div>
        <div className="card card-pad stat">
          <span className="tiny muted">Поставлено всего</span>
          <span className="stat-value" style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
            <span>{formatRub(wagered)}</span>
            <CoinIcon size="sm" />
          </span>
        </div>
        <div className="card card-pad stat">
          <span className="tiny muted">Получено предметами</span>
          <span className="stat-value" style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
            <span>{formatRub(won)}</span>
            <CoinIcon size="sm" />
          </span>
        </div>
        <div className="card card-pad stat">
          <span className="tiny muted">Личная отдача</span>
          <span className={`stat-value ${net >= 0 ? 'is-plus' : 'is-minus'}`}>
            {wagered > 0 ? `${(ratio * 100).toFixed(1)}%` : '—'}
          </span>
          <span className="tiny muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span>{net >= 0 ? '+' : '−'}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
              <span>{formatRub(Math.abs(net))}</span>
              <CoinIcon size="xs" />
            </span>
            <span>к вложенному</span>
          </span>
        </div>
      </div>

      <section className="card card-pad profile-trade-section" style={{ marginBottom: 24, border: '1px solid rgba(56, 189, 248, 0.25)' }}>
        <div className="row wrap justify-between items-center" style={{ marginBottom: 10 }}>
          <div>
            <span className="eyebrow" style={{ color: 'var(--cyan-400, #38bdf8)' }}>Market.CSGO Delivery</span>
            <h3 style={{ margin: '4px 0 0 0' }}>Ссылка на обмен Steam (Trade URL)</h3>
          </div>
          <span className={`pill ${tradeUrl ? 'status-owned' : 'status-pending'}`}>
            {tradeUrl ? 'Ссылка настроена' : 'Не указана'}
          </span>
        </div>
        <p className="muted tiny" style={{ marginBottom: 14 }}>
          Укажите вашу Trade-ссылку для автоматического вывода скинов в инвентарь Steam через сервис Market.CSGO.
        </p>
        <form onSubmit={handleSaveTradeUrl} className="row wrap" style={{ gap: 10 }}>
          <input
            type="text"
            className="input"
            style={{ flex: 1, minWidth: 280 }}
            placeholder="https://steamcommunity.com/tradeoffer/new/?partner=...&token=..."
            value={tradeUrl}
            onChange={(e) => setTradeUrl(e.target.value)}
          />
          <button type="submit" className="btn btn-primary" disabled={savingTradeUrl || !tradeUrl.trim()}>
            {savingTradeUrl ? 'Сохраняем…' : 'Сохранить ссылку'}
          </button>
        </form>
        <div style={{ marginTop: 10 }}>
          <a
            href="https://steamcommunity.com/my/tradeoffers/privacy#trade_offer_access_url"
            target="_blank"
            rel="noopener noreferrer"
            className="tiny link muted"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <span>Где найти ссылку на обмен в Steam? ↗</span>
          </a>
        </div>
      </section>

      <div className="profile-layout">
        <section className="card card-pad">
          <h3 style={{ marginBottom: 16 }}>Откуда предметы</h3>

          {!stats ? (
            <div className="skeleton" style={{ height: 160 }} />
          ) : stats.bySource.length === 0 ? (
            <p className="muted tiny">Предметов пока нет.</p>
          ) : (
            <div className="source-list">
              {stats.bySource
                .slice()
                .sort((a, b) => b.totalKopecks - a.totalKopecks)
                .map((row) => (
                  <div key={row.source} className="source-row">
                    <span className="strong tiny">{sourceLabel(row.source)}</span>
                    <span className="tiny muted">
                      {row.count} {plural(row.count, 'предмет', 'предмета', 'предметов')}
                    </span>
                    <span className="tiny strong" style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                      <span>{formatRub(row.totalKopecks)}</span>
                      <CoinIcon size="xs" />
                    </span>
                  </div>
                ))}
            </div>
          )}

          {stats?.bestDrop && bestItem && (
            <>
              <hr className="divider" />
              <h3 style={{ marginBottom: 12 }}>Лучший дроп</h3>
              <div className="best-drop">
                <WeaponArt image={bestItem.image} rarity={bestItem.rarity} size="sm" />
                <div className="best-info">
                  <span className="strong">
                    {bestItem.name} | {bestItem.skin}
                  </span>
                  <span className="tiny muted">
                    {RARITY_LABELS[bestItem.rarity]} ·{' '}
                    {WEAR_SHORT[stats.bestDrop.wear] ?? stats.bestDrop.wear} ·{' '}
                    {formatDate(stats.bestDrop.createdAt)}
                  </span>
                </div>
                <span className="best-price" style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                  <span>{formatRub(stats.bestDrop.priceKopecks)}</span>
                  <CoinIcon size="sm" />
                </span>
              </div>
            </>
          )}
        </section>

        <section className="card profile-tx">
          <div className="profile-tx-head">
            <h3>Движение средств</h3>
          </div>

          {!stats ? (
            <div className="skeleton" style={{ height: 300, margin: 18 }} />
          ) : stats.transactions.length === 0 ? (
            <p className="muted tiny center" style={{ padding: 28 }}>
              Операций пока нет.
            </p>
          ) : (
            <div className="tx-list">
              {stats.transactions.map((tx, index) => (
                <div key={index} className="tx-row">
                  <span className="tx-what">
                    <span className="tiny strong">{txLabel(tx.type)}</span>
                    <span className="tiny muted">{formatDate(tx.createdAt)}</span>
                  </span>
                  <span className={`tx-amount ${tx.amount >= 0 ? 'is-plus' : 'is-minus'}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <span>{tx.amount >= 0 ? '+' : '−'}</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                      <span>{formatRub(Math.abs(tx.amount))}</span>
                      <CoinIcon size="xs" />
                    </span>
                  </span>
                  <span className="tiny muted tx-after" style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                    <span>{formatRub(tx.balanceAfter)}</span>
                    <CoinIcon size="xs" />
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
